/**
 * Notification utilities (Migrated from react-hot-toast)
 *
 * @deprecated Import useNotification hook instead for React components
 * This file provides backward compatibility during migration
 */

import { useToastStore } from '@shared/store/toastStore';
import { triggerHaptic } from '@shared/utils/haptics';

/**
 * Non-hook toast API for use outside React components
 * For use in stores, utilities, and other non-React contexts
 */
export const toast = {
  success: (message: string) => {
    triggerHaptic('success');
    return useToastStore.getState().addToast({ message, variant: 'success' });
  },
  error: (message: string) => {
    triggerHaptic('error');
    return useToastStore.getState().addToast({ message, variant: 'error', duration: 7000 });
  },
  warning: (message: string) => {
    triggerHaptic('warning');
    return useToastStore.getState().addToast({ message, variant: 'warning' });
  },
  info: (message: string) => {
    triggerHaptic('light');
    return useToastStore.getState().addToast({ message, variant: 'info' });
  },
  loading: (message: string) => {
    return useToastStore.getState().addToast({
      message,
      variant: 'info',
      title: 'Loading...',
      duration: 0
    });
  },
  dismiss: (toastId?: string) => {
    if (toastId) {
      useToastStore.getState().removeToast(toastId);
    } else {
      useToastStore.getState().clearAll();
    }
  },
};

/**
 * Promise-based toast utility for async operations
 * Shows loading, success, or error based on promise result
 */
export async function toastPromise<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string;
    error: string;
  }
): Promise<T> {
  const loadingId = toast.loading(messages.loading);

  try {
    const result = await promise;
    toast.dismiss(loadingId);
    toast.success(messages.success);
    return result;
  } catch (error) {
    toast.dismiss(loadingId);
    toast.error(messages.error);
    throw error;
  }
}

/**
 * Re-export new notification components
 */
export { ToastContainer } from '@shared/components/ui/Toast';
export { useNotification } from '@shared/hooks/useNotification';
